import React, { useEffect } from 'react';
import { LinkLegacy as RouterLink } from '@leagueplatform/routing';
import { Activity, Achievement } from '#constants/types.ts';
import { useIntl } from '@leagueplatform/locales';
import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  useConfigProperty,
} from '@leagueplatform/core';
import { track } from '@leagueplatform/analytics';
import {
  Button,
  HeadingText,
  Modal,
  StackLayout,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';

import { BadgeProgress } from '../badge-progress/badge-progress.view';

interface ProgressModalProps {
  onClose: () => void;
  activities: Activity[];
  celebrations?: Achievement[];
}

export const ProgressModal = ({
  onClose,
  activities,
  celebrations,
}: ProgressModalProps) => {
  const { formatMessage } = useIntl();
  const disableAchievementsPageLinks = useConfigProperty(
    'rewardsEvents.disableAchievementsPageLinks',
    false,
  );

  const handleViewAllClick = () => {
    track('rewards.button_clicked.progress_modal.view_all');
    onClose();
  };

  useEffect(() => {
    track('rewards.screen_loaded.progress_modal');
  }, [activities]);

  return (
    <Modal.Root
      defaultOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Modal.Title>
        <VisuallyHidden>
          {formatMessage({ id: 'ACHIEVEMENT_PROGRESS' })}
        </VisuallyHidden>
      </Modal.Title>

      <Modal.Content
        css={{
          '.GDS-modal': { width: '100%', maxWidth: '618px' },
        }}
      >
        <StackLayout orientation="vertical" horizontalAlignment="center">
          <HeadingText level="1" size="xxl" css={{ marginBottom: '$two' }}>
            {formatMessage({ id: 'KEEP_GOING' })}!
          </HeadingText>
          <StackLayout
            orientation="vertical"
            horizontalAlignment="center"
            css={{ width: '100%', maxWidth: '350px', marginBottom: '$two' }}
          >
            {activities.map((activity) => (
              <BadgeProgress
                imageUrl={activity.image.large}
                isLoading={false}
                title={activity.name}
                totalActivities={activity.progress.threshold}
                currentActivity={activity.progress.current}
                progressTitle={`${activity.progress.current}/${
                  activity.progress.threshold
                } ${formatMessage({ id: 'ACTIVITIES' })}`}
                completed={activity.progress.is_completed}
              />
            ))}
          </StackLayout>
          <StackLayout orientation="horizontal" spacing="$one">
            {celebrations ? (
              <Button onClick={onClose}>
                {formatMessage({ id: 'CONTINUE' })}
              </Button>
            ) : (
              <>
                {!disableAchievementsPageLinks && (
                  <Button
                    priority="tertiary"
                    quiet
                    role="link"
                    as={RouterLink}
                    to={getModulePath(LEAGUE_MODULE_NAMES.rewardsAchievements)}
                    onClick={handleViewAllClick}
                    css={{
                      textDecoration: 'none',
                      marginInlineEnd: '$oneAndHalf',
                    }}
                  >
                    {formatMessage({ id: 'VIEW_ALL_ACHIEVEMENTS' })}
                  </Button>
                )}
                <Button onClick={onClose}>
                  {formatMessage({ id: 'TAKE_ME_BACK' })}
                </Button>
              </>
            )}
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
